import { AnalyticsProvider, AuthProvider, useAuth } from '@hologram-hyper-dashboard/components';
import { sendDatadogAction, sendDatadogError } from 'common-js/analytics/dataDog';
import { getUserContextDataMemoized } from 'common-js/api/util';
import useAppSelector from 'common-js/hooks/useAppSelector';
import useStore from 'common-js/useStore';
import { useEffect, useRef, type FC, type ReactNode } from 'react';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';
import { Helmet } from 'react-helmet';
import DeviceContextProvider from './DeviceContextProvider';
import HoloStoreContextProvider from './HoloStoreContextProvider';
import Usetiful from './Usetiful';
import ErrorBoundary from './ErrorBoundary/ErrorBoundary';

interface HoloStoreInnerProps extends HoloStoreProps {}

const HoloStoreInner: FC<HoloStoreInnerProps> = ({ children }) => {
  const userContext = useAppSelector(getUserContextDataMemoized);
  const orgId = userContext.isInOrgContext ? userContext.orgId : userContext.userOrgId;
  const { invalidate } = useAuth();

  useEffect(() => {
    invalidate();
  }, [invalidate, orgId]);

  return children;
};

interface HoloStoreProps {
  children?: ReactNode;
}

const HoloStore: FC<HoloStoreProps> = ({ children }) => {
  const { store, persistor } = useStore();

  const alertBannerContainerRef = useRef<HTMLDivElement>(null);
  const holoStoreRef = useRef<HTMLDivElement>(null);

  let favicon: string = '';
  switch (process.env.VITE_ENV_NAME) {
    case 'staging':
      favicon = '/assets/img/favicons/favicon-staging.svg';
      break;
    case 'production':
      favicon = '/assets/img/favicons/favicon-prod.svg';
      break;
    case 'mr':
    default:
      favicon = '/assets/img/favicons/favicon-feature-branch.svg';
      break;
  }

  return (
    <Provider store={store}>
      <Helmet>
        <link rel="icon" href={favicon} type="image/svg+xml" />
      </Helmet>
      <PersistGate loading={null} persistor={persistor}>
        <div className="site-banners" ref={alertBannerContainerRef} />
        <div className="HoloStore full-height" ref={holoStoreRef}>
          <HoloStoreContextProvider
            alertBannerContainerRef={alertBannerContainerRef}
            holoStoreRef={holoStoreRef}
          >
            <AnalyticsProvider
              analyticsContext={{
                sendAnalyticsEvent: sendDatadogAction,
                sendAnalyticsError: sendDatadogError,
              }}
            >
              <AuthProvider>
                <DeviceContextProvider>
                  <HoloStoreInner>{children}</HoloStoreInner>
                </DeviceContextProvider>
              </AuthProvider>
            </AnalyticsProvider>
          </HoloStoreContextProvider>
        </div>
        <ErrorBoundary showErrorWrapper={false}>
          <Usetiful />
        </ErrorBoundary>
      </PersistGate>
    </Provider>
  );
};

export default HoloStore;
